import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Briefcase, Palette, Code, GraduationCap, Video, 
  Music, MessageSquare, Home, Sparkles, FileText
} from 'lucide-react';

interface UseCaseNavigationProps {
  activeUseCase: string;
  onUseCaseChange: (useCase: string) => void;
}

const useCases = [
  { id: 'all', label: 'All Tools', icon: Sparkles, color: 'from-primary to-accent', link: null },
  { id: 'productivity', label: 'Productivity', icon: Briefcase, color: 'from-blue-500 to-cyan-500', link: '/category/productivity' },
  { id: 'creative', label: 'Creative', icon: Palette, color: 'from-pink-500 to-rose-500', link: '/category/creative' },
  { id: 'coding', label: 'Development', icon: Code, color: 'from-green-500 to-emerald-500', link: '/category/coding' },
  { id: 'learning', label: 'Learning', icon: GraduationCap, color: 'from-yellow-500 to-orange-500', link: '/category/learning' },
  { id: 'video', label: 'Video', icon: Video, color: 'from-red-500 to-pink-500', link: '/category/video' },
  { id: 'audio', label: 'Audio', icon: Music, color: 'from-purple-500 to-violet-500', link: '/category/audio' },
  { id: 'chat', label: 'Chat & Assistant', icon: MessageSquare, color: 'from-indigo-500 to-blue-500', link: '/category/chat' },
  { id: 'design', label: 'Design', icon: Home, color: 'from-teal-500 to-cyan-500', link: '/category/design' },
  { id: 'writing', label: 'Writing', icon: FileText, color: 'from-amber-500 to-yellow-500', link: '/category/writing' },
];

export const UseCaseNavigation: React.FC<UseCaseNavigationProps> = ({
  activeUseCase,
  onUseCaseChange,
}) => {
  return (
    <section className="px-4 py-6">
      <div className="container mx-auto">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Find AI for...
        </h3>
        
        <div className="flex flex-wrap gap-2">
          {useCases.map((useCase, index) => {
            const isActive = activeUseCase === useCase.id;
            const Icon = useCase.icon;
            
            const buttonContent = (
              <motion.button
                onClick={() => !useCase.link && onUseCaseChange(useCase.id)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium
                  transition-all duration-300 border
                  ${isActive 
                    ? `bg-gradient-to-r ${useCase.color} text-white border-transparent shadow-lg` 
                    : 'glass border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/30'
                  }
                `}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-white' : ''}`} />
                <span>{useCase.label}</span>
              </motion.button>
            );
            
            // If has SEO link, wrap in Link for categories
            if (useCase.link) {
              return (
                <Link key={useCase.id} to={useCase.link}>
                  {buttonContent}
                </Link>
              );
            }
            
            return <div key={useCase.id}>{buttonContent}</div>;
          })}
        </div>
      </div>
    </section>
  );
};

export default UseCaseNavigation;
